import React, { useState, useRef } from 'react';
import {
  PhotoIcon,
  VideoCameraIcon,
  CodeBracketIcon,
  EyeIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import type { AdCreativeData, AdDisplayType } from '../../types/campaign';
import { DISPLAY_TYPE_CONFIGS } from '../../types/campaign';

interface AdCreativeDesignerProps {
  creative: Partial<AdCreativeData>;
  onChange: (creative: Partial<AdCreativeData>) => void;
  onRemove?: () => void;
  showRemove?: boolean;
}

const AdCreativeDesigner: React.FC<AdCreativeDesignerProps> = ({
  creative,
  onChange,
  onRemove,
  showRemove = false
}) => {
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const displayTypeConfig = creative.displayType ? DISPLAY_TYPE_CONFIGS[creative.displayType] : null;

  const handleFieldChange = (field: keyof AdCreativeData, value: any) => {
    onChange({ ...creative, [field]: value });
  };

  const handleImpressionTrackingUrlAdd = (url: string) => {
    if (url.trim()) {
      const currentUrls = creative.impressionTrackingUrls || [];
      handleFieldChange('impressionTrackingUrls', [...currentUrls, url.trim()]);
    }
  };

  const handleImpressionTrackingUrlRemove = (index: number) => {
    const currentUrls = creative.impressionTrackingUrls || [];
    const newUrls = currentUrls.filter((_, i) => i !== index);
    handleFieldChange('impressionTrackingUrls', newUrls);
  };

  const handleClickTrackingUrlAdd = (url: string) => {
    if (url.trim()) {
      const currentUrls = creative.clickTrackingUrls || [];
      handleFieldChange('clickTrackingUrls', [...currentUrls, url.trim()]);
    }
  };

  const handleClickTrackingUrlRemove = (index: number) => {
    const currentUrls = creative.clickTrackingUrls || [];
    const newUrls = currentUrls.filter((_, i) => i !== index);
    handleFieldChange('clickTrackingUrls', newUrls);
  };



  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      // TODO: Implement actual file upload
      // For now, create a local URL for preview
      const imageUrl = URL.createObjectURL(file);
      handleFieldChange('imageUrl', imageUrl);
    } catch (error) {
      console.error('Failed to upload image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingVideo(true);
    try {
      // TODO: Implement actual file upload
      // For now, create a local URL for preview
      const videoUrl = URL.createObjectURL(file);
      handleFieldChange('videoUrl', videoUrl);
    } catch (error) {
      console.error('Failed to upload video:', error);
    } finally {
      setUploadingVideo(false);
    }
  };

  const renderPreview = () => {
    if (!creative.displayType || !displayTypeConfig) {
      return (
        <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
          <p className="text-gray-500">Görüntü tipi seçin</p>
        </div>
      );
    }

    const { width, height } = displayTypeConfig.dimensions;
    const aspectRatio = width / height;
    const maxWidth = previewMode === 'desktop' ? 400 : 300;
    const previewWidth = Math.min(maxWidth, width);
    const previewHeight = previewWidth / aspectRatio;

    const containerStyle = {
      width: `${previewWidth}px`,
      height: `${previewHeight}px`,
      maxWidth: '100%'
    };

    switch (creative.displayType) {
      case 'banner':
      case 'l_banner':
      case 'square_banner':
      case 'bottom_banner':
        return (
          <div 
            className="relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white"
            style={containerStyle}
          >
            {creative.imageUrl ? (
              <img
                src={creative.imageUrl}
                alt={creative.title || 'Ad Preview'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <PhotoIcon className="h-12 w-12 mb-2" />
                <p className="text-sm">Görsel yükleyin</p>
              </div>
            )}
            {creative.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-2">
                <p className="text-sm font-medium truncate">{creative.title}</p>
                {creative.description && (
                  <p className="text-xs opacity-90 truncate">{creative.description}</p>
                )}
              </div>
            )}
          </div>
        );

      case 'video_overlay':
        return (
          <div 
            className="relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-black"
            style={containerStyle}
          >
            {creative.videoUrl ? (
              <video
                src={creative.videoUrl}
                className="w-full h-full object-cover"
                controls
                muted
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <VideoCameraIcon className="h-12 w-12 mb-2" />
                <p className="text-sm">Video yükleyin</p>
              </div>
            )}
          </div>
        );

      case 'third_party':
        return (
          <div 
            className="relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50"
            style={containerStyle}
          >
            {creative.adServerTag ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-600">
                <CodeBracketIcon className="h-12 w-12 mb-2" />
                <p className="text-sm font-medium">Üçüncü Taraf Reklam</p>
                <p className="text-xs opacity-75">Ad server tag aktif</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <CodeBracketIcon className="h-12 w-12 mb-2" />
                <p className="text-sm">Ad server tag ekleyin</p>
              </div>
            )}
          </div>
        );

      case 'full_page':
        return (
          <div 
            className="relative border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white"
            style={containerStyle}
          >
            {creative.imageUrl || creative.videoUrl ? (
              <>
                {creative.videoUrl ? (
                  <video
                    src={creative.videoUrl}
                    className="w-full h-full object-cover"
                    controls
                    muted
                  />
                ) : (
                  <img
                    src={creative.imageUrl}
                    alt={creative.title || 'Ad Preview'}
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="text-center text-white">
                    <h3 className="text-lg font-bold mb-2">{creative.title || 'Tam Sayfa Reklam'}</h3>
                    {creative.description && (
                      <p className="text-sm opacity-90">{creative.description}</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <PhotoIcon className="h-12 w-12 mb-2" />
                <p className="text-sm">Görsel veya video yükleyin</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
            <p className="text-gray-500">Önizleme mevcut değil</p>
          </div>
        );
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">
          Reklam Kreatifi {creative.name && `- ${creative.name}`}
        </h3>
        {showRemove && onRemove && (
          <button
            onClick={onRemove}
            className="text-red-600 hover:text-red-800"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Fields */}
        <div className="space-y-4">
          {/* Basic Info */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Kreatif Adı *
            </label>
            <input
              type="text"
              id="name"
              value={creative.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Kreatif adını girin"
            />
          </div>

          {/* Display Type */}
          <div>
            <label htmlFor="displayType" className="block text-sm font-medium text-gray-700 mb-1">
              Görüntü Tipi *
            </label>
            <select
              id="displayType"
              value={creative.displayType || ''}
              onChange={(e) => handleFieldChange('displayType', e.target.value as AdDisplayType)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Seçin</option>
              {Object.entries(DISPLAY_TYPE_CONFIGS).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.name} ({config.dimensions.aspectRatio})
                </option>
              ))}
            </select>
            {displayTypeConfig && (
              <p className="mt-1 text-xs text-gray-500">
                {displayTypeConfig.description} - Önerilen boyut: {displayTypeConfig.dimensions.width}x{displayTypeConfig.dimensions.height}
              </p>
            )}
          </div>

          {/* Title and Description */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Başlık
            </label>
            <input
              type="text"
              id="title"
              value={creative.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Reklam başlığı"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Açıklama
            </label>
            <textarea
              id="description"
              rows={3}
              value={creative.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Reklam açıklaması"
            />
          </div>

          {/* Media Upload */}
          {creative.displayType && ['banner', 'l_banner', 'square_banner', 'bottom_banner', 'full_page'].includes(creative.displayType) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Görsel
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="url"
                  value={creative.imageUrl || ''}
                  onChange={(e) => handleFieldChange('imageUrl', e.target.value)}
                  className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Görsel URL'si"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                  {uploadingImage ? 'Yükleniyor...' : 'Yükle'}
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          )}

          {creative.displayType && ['video_overlay', 'full_page'].includes(creative.displayType) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Video
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="url"
                  value={creative.videoUrl || ''}
                  onChange={(e) => handleFieldChange('videoUrl', e.target.value)}
                  className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Video URL'si"
                />
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploadingVideo}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-1" />
                  {uploadingVideo ? 'Yükleniyor...' : 'Yükle'}
                </button>
              </div>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="hidden"
              />
            </div>
          )}

          {/* Third Party Ad Server Tag */}
          {creative.displayType === 'third_party' && (
            <div>
              <label htmlFor="adServerTag" className="block text-sm font-medium text-gray-700 mb-1">
                Ad Server Tag *
              </label>
              <textarea
                id="adServerTag"
                rows={4}
                value={creative.adServerTag || ''}
                onChange={(e) => handleFieldChange('adServerTag', e.target.value)}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono text-xs"
                placeholder="<script>...</script> veya HTML tag"
              />
            </div>
          )}

          {/* Click URL */}
          <div>
            <label htmlFor="clickUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Tıklama URL'si
            </label>
            <input
              type="url"
              id="clickUrl"
              value={creative.clickUrl || ''}
              onChange={(e) => handleFieldChange('clickUrl', e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Tıklama URL'si girin"
            />
          </div>

          {/* Tracking URLs Section */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-900">Tracking URL'leri</h4>
            </div>

            {/* Impression Tracking URLs */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Impression Tracking URL'leri
              </label>
              <div className="space-y-2">
                <div className="flex">
                  <input
                    type="url"
                    placeholder="Impression tracking URL'si ekle"
                    className="flex-1 border-gray-300 rounded-l-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleImpressionTrackingUrlAdd(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                      handleImpressionTrackingUrlAdd(input.value);
                      input.value = '';
                    }}
                    className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-1">
                  {creative.impressionTrackingUrls?.map((url, index) => (
                    <div key={index} className="flex items-center bg-gray-50 rounded-md p-2">
                      <span className="flex-1 text-xs text-gray-600 truncate" title={url}>
                        {url}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleImpressionTrackingUrlRemove(index)}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Click Tracking URLs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Click Tracking URL'leri
              </label>
              <div className="space-y-2">
                <div className="flex">
                  <input
                    type="url"
                    placeholder="Click tracking URL'si ekle"
                    className="flex-1 border-gray-300 rounded-l-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleClickTrackingUrlAdd(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                      handleClickTrackingUrlAdd(input.value);
                      input.value = '';
                    }}
                    className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="space-y-1">
                  {creative.clickTrackingUrls?.map((url, index) => (
                    <div key={index} className="flex items-center bg-gray-50 rounded-md p-2">
                      <span className="flex-1 text-xs text-gray-600 truncate" title={url}>
                        {url}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleClickTrackingUrlRemove(index)}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900 flex items-center">
              <EyeIcon className="h-4 w-4 mr-1" />
              Önizleme
            </h4>
            <div className="flex rounded-md shadow-sm">
              <button
                type="button"
                onClick={() => setPreviewMode('desktop')}
                className={`px-3 py-1 text-xs font-medium rounded-l-md border ${
                  previewMode === 'desktop'
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Masaüstü
              </button>
              <button
                type="button"
                onClick={() => setPreviewMode('mobile')}
                className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-r border-b ${
                  previewMode === 'mobile'
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Mobil
              </button>
            </div>
          </div>

          <div className="flex justify-center">
            {renderPreview()}
          </div>

          {displayTypeConfig && (
            <div className="bg-gray-50 rounded-lg p-3">
              <h5 className="text-xs font-medium text-gray-900 mb-2">Gereksinimler:</h5>
              <ul className="text-xs text-gray-600 space-y-1">
                {displayTypeConfig.requirements.map((req, index) => (
                  <li key={index}>• {req}</li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Desteklenen formatlar: {displayTypeConfig.supportedFormats.join(', ')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdCreativeDesigner;